import { computed, ref, watch, type Ref } from 'vue'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import {
  referenceFieldTypesFromClasses,
  type ObjectTypeFieldsResponse,
  type PassportBuilderObjectTypeFieldDto,
  type PassportBuilderObjectTypeFieldPayload,
} from './objectTypeFields'

export type RelationRow = {
  id: number
  name: string
  reverseName: string
  toTypeId: number
  cardinality: string
  mirrorFieldId: number | null
  isRequired: boolean
  sort: number
}

export type NewRelationDraft = {
  name: string
  reverseName: string
  toTypeId: number
  cardinality: string
  isRequired: boolean
}

const EMPTY_DRAFT: NewRelationDraft = {
  name: '',
  reverseName: '',
  toTypeId: 0,
  cardinality: '',
  isRequired: false,
}

const REFERENCE_TYPE_TITLES_BY_NAME: Record<string, string> = {
  one_to_one: 'itf.passport_builder_link_one_to_one',
  one_to_many: 'itf.passport_builder_link_one_to_many',
  many_to_one: 'itf.passport_builder_link_many_to_one',
  many_to_many: 'itf.passport_builder_link_many_to_many',
}

export function usePassportBuilderLinks(
  fromTypeId: Readonly<Ref<number>>,
  refetchFn: () => Promise<unknown>,
  objectTypeFieldsResponse: Readonly<Ref<ObjectTypeFieldsResponse>>,
  enabled?: Readonly<Ref<boolean>>,
) {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const rows = ref<RelationRow[]>([])
  const saving = ref(false)
  const draft = ref<NewRelationDraft>({ ...EMPTY_DRAFT })

  const isEmpty = computed(() => rows.value.length === 0)
  const referenceFieldTypes = computed(() =>
    referenceFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
  )
  const referenceTypeNames = computed(() =>
    new Set(referenceFieldTypes.value.map((item) => item.name)),
  )
  const referenceTypeByName = computed(() =>
    new Map(referenceFieldTypes.value.map((item) => [item.name, item])),
  )
  const referenceTypeById = computed(() =>
    new Map(referenceFieldTypes.value.map((item) => [item.id, item])),
  )
  const referenceTypeItems = computed(() =>
    referenceFieldTypes.value.map((item) => ({
      title: REFERENCE_TYPE_TITLES_BY_NAME[item.name]
        ? trs(REFERENCE_TYPE_TITLES_BY_NAME[item.name])
        : item.name,
      value: item.name,
    })),
  )

  /**
   * Преобразует reference-поле новой модели в строку модалки связей.
   */
  function toRow(api: PassportBuilderObjectTypeFieldDto): RelationRow {
    return {
      id: api.id,
      name: api.name ?? '',
      reverseName: api.reverse_name ?? api.mirror_field?.name ?? '',
      toTypeId: Number(api.reference_id) || 0,
      cardinality: referenceTypeById.value.get(api.field_type)?.name ?? '',
      mirrorFieldId: api.mirror_field_id ?? null,
      isRequired: Boolean(api.is_required),
      sort: Number(api.sort) || 0,
    }
  }

  /**
   * Загружает связи как reference-поля типа объекта.
   */
  async function loadRows() {
    if (fromTypeId.value <= 0) {
      rows.value = []
      return
    }

    const normalized = objectTypeFieldsResponse.value
    const referenceTypeIds = new Set(
      referenceFieldTypesFromClasses(normalized.fieldClasses).map((item) => item.id),
    )
    rows.value = normalized.fields
      .filter((item) => referenceTypeIds.has(item.field_type) || Number(item.reference_id) > 0)
      .map(toRow)
  }

  watch(
    () => [fromTypeId.value, enabled?.value ?? true, objectTypeFieldsResponse.value] as const,
    ([, isEnabled]) => {
      if (isEnabled && fromTypeId.value > 0) loadRows()
    },
    { immediate: true, deep: true },
  )

  function validateName(name: string): boolean {
    return name.trim().length > 0
  }

  function validateRow(row: RelationRow): string | null {
    if (!validateName(row.name)) return 'validate.relation_name_required'
    if (!validateName(row.reverseName)) return 'validate.relation_reverse_name_required'
    if (row.toTypeId <= 0) return 'validate.relation_target_type_required'
    if (row.toTypeId === fromTypeId.value) return 'validate.relation_target_type_same'
    if (!row.cardinality || !referenceTypeNames.value.has(row.cardinality)) {
      return 'validate.relation_cardinality_required'
    }
    return null
  }

  function validateDraft(d: NewRelationDraft): string | null {
    if (!validateName(d.name)) return 'validate.relation_name_required'
    if (!validateName(d.reverseName)) return 'validate.relation_reverse_name_required'
    if (d.toTypeId <= 0) return 'validate.relation_target_type_required'
    if (d.toTypeId === fromTypeId.value) return 'validate.relation_target_type_same'
    if (!d.cardinality || !referenceTypeNames.value.has(d.cardinality)) {
      return 'validate.relation_cardinality_required'
    }
    return null
  }

  /**
   * Возвращает следующий порядок поля без предположения, что sort идет без дыр.
   */
  function nextSort(): number {
    return Math.max(0, ...rows.value.map((item) => Number(item.sort) || 0)) + 1
  }

  /**
   * Готовит payload reference-поля; обратное имя уходит как `reverse_name`
   * рядом с `name`, чтобы backend мог создать или обновить зеркальное поле.
   */
  function apiPayload(row: RelationRow | NewRelationDraft): PassportBuilderObjectTypeFieldPayload {
    const fieldType = referenceTypeByName.value.get(row.cardinality)

    return {
      catalog_objects_type_id: fromTypeId.value,
      field_type: Number(fieldType?.id) || 0,
      name: row.name.trim(),
      reverse_name: row.reverseName.trim(),
      catalog_id: null,
      reference_id: row.toTypeId,
      mirror_field_id: 'mirrorFieldId' in row ? row.mirrorFieldId : null,
      default_value: null,
      is_system: false,
      is_required: Boolean(row.isRequired),
      use_in_filter: false,
      use_in_search: false,
      validation_props: {},
      sort: 'sort' in row && row.sort > 0 ? row.sort : nextSort(),
    }
  }

  async function saveRow(row: RelationRow): Promise<boolean> {
    const error = validateRow(row)
    if (error) {
      addError(trs(error))
      return false
    }
    saving.value = true
    try {
      await http.put(`/object-types/${fromTypeId.value}/fields/${row.id}`, apiPayload(row))
      addSuccess(trs('itf.saved'))
      await refetchFn()
      await loadRows()
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    } finally {
      saving.value = false
    }
  }

  async function createRow(): Promise<boolean> {
    const error = validateDraft(draft.value)
    if (error) {
      addError(trs(error))
      return false
    }
    saving.value = true
    try {
      await http.post(`/object-types/${fromTypeId.value}/fields`, apiPayload(draft.value))
      addSuccess(trs('itf.saved'))
      draft.value = { ...EMPTY_DRAFT }
      await refetchFn()
      await loadRows()
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    } finally {
      saving.value = false
    }
  }

  async function deleteRow(id: number): Promise<boolean> {
    saving.value = true
    try {
      await http.del(`/object-types/${fromTypeId.value}/fields/${id}`)
      await refetchFn()
      await loadRows()
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    } finally {
      saving.value = false
    }
  }

  return {
    rows,
    draft,
    loading: computed(() => false),
    saving,
    isEmpty,
    referenceTypeItems,
    loadRows,
    saveRow,
    createRow,
    deleteRow,
    validateRow,
    validateDraft,
  }
}
